import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import AddVisitModal from './AddVisitModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import StaffLayout, { PageHeading, PersonName, EmptyState } from '../../components/StaffLayout';
import ArrowBack from '@mui/icons-material/ArrowBack';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import { formatClinicDate } from '../../components/VisitDirectory';
import { getPatientDetails, getPatientVisits } from '../../services/patientService';
import { deleteVisit } from '../../services/visitService';
import { FaPlus, FaPrint, FaFilePdf, FaEdit, FaTrash } from "react-icons/fa";
import { Alert, Button, CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import EditVisitModal from "./EditVisitsModal";

// Function to convert new lines to <br />
// const formatTextWithNewLines = (text) => {
//     return text ? text.split('\n').map((str, index) => <span key={index}>{str}<br /></span>) : '';
// };

const PatientRecords = () => {
    const { id } = useParams(); // Get patient ID from URL
    const [patient, setPatient] = useState(null);
    const [visits, setVisits] = useState([]); // Store the list of visits
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState(null); // To track the visit being edited
    const [showEditVisitModal, setShowEditVisitModal] = useState(false); // For edit visit modal
    const [showDeleteModal, setShowDeleteModal] = useState(false); // For delete confirmation modal

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [visitsError, setVisitsError] = useState('');
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setVisitsError('');
        const fetchPatient = async () => {
            const response = await getPatientDetails(id);
            if (!active) return;
            if (response.success && response.data) {
                setPatient(response.data);
            } else {
                setPatient(null);
                setError('Unable to load this patient. Please try again.');
            }
        };

        const fetchVisits = async () => {
            const response = await getPatientVisits(id); // Fetch patient visits
            if (!active) return;
            if (response.success && response.data) {
                setVisits(Array.isArray(response.data) ? response.data : []);
            } else {
                setVisitsError('Unable to load visit history. Please try again.');
                setVisits([]); // Set empty array on error
            }
        };

        Promise.all([fetchPatient(), fetchVisits()]).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [id, retry]);

    // Update visits after a new visit is added
    const handleVisitAdded = (newVisit) => {
        setVisits((prevVisits) => [newVisit, ...prevVisits]);
    };

    // Update visits after a visit is edited
    const handleVisitEdited = (editedVisit) => {
        setVisits((prevVisits) =>
            prevVisits.map((visit) => (String(visit.id) === String(editedVisit.id) ? editedVisit : visit))
        );
    };

    // Delete visit
    const handleDeleteVisit = async () => {
        try {
            const response = await deleteVisit(selectedVisit.id);
            if (!response.success || response.data?.status !== 'success') throw new Error();
            setVisits((prevVisits) => prevVisits.filter((visit) => visit.id !== selectedVisit.id)); // Update UI
            setShowDeleteModal(false); // Close the delete confirmation modal
        } catch (error) {
            setVisitsError('Unable to delete visit. Please try again.');
            setShowDeleteModal(false);
        }
    };

    if (!patient) {
        return <StaffLayout><PageHeading title="Patient records" description="A complete picture of your patient's care." />{loading ? <div className="staff-loading" role="status"><CircularProgress size={24} />Loading patient records...</div> : <Alert severity="error" action={<Button onClick={() => setRetry(n => n + 1)}>Retry</Button>}>{error}</Alert>}</StaffLayout>;
    }

    // Function to format the date
    const formatVisitDate = (date) => {
        return date ? format(new Date(date), 'dd.MM.yyyy') : 'No visits yet';
    };

    // Function to handle Print
// Function to handle Print
    const handlePrint = (visit) => {
        const printContent = `
    <div style="padding: 20px; font-family: Arial, sans-serif; position: relative; min-height: 90vh;">
        <h1>Patient: ${patient.name} ${patient.lastname}</h1>
        <p><strong>Visit Date:</strong> ${formatVisitDate(visit.visit_date)}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />
        <p><strong>Diagnose:</strong><br />${visit.diagnose ? visit.diagnose.replace(/\n/g, '<br />') : 'No data available'}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />
        <p><strong>ECHO Report:</strong><br />${visit.echo_report ? visit.echo_report.replace(/\n/g, '<br />') : 'No data available'}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />
        <p><strong>Treatment:</strong><br />${visit.treatment ? visit.treatment.replace(/\n/g, '<br />') : 'No data available'}</p>
        <hr style="border: 1px solid black; margin: 20px 0;" />

        <div style="position: absolute; bottom: 0; right: 0; font-weight: bold; margin: 20px;">
            Dr. Spec. Endocrinolog: Beqir Mahmudi
        </div>
    </div>
    `;
        const newWindow = window.open('', '', 'width=800,height=600');
        newWindow.document.write(`<html><body>${printContent}</body></html>`);
        newWindow.document.close();
        newWindow.print();
    };


    // Function to handle PDF generation
// Function to handle PDF generation
    const handlePDF = (visit) => {
        const doc = new jsPDF();
        let yPosition = 10;

        // Title
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(`Patient: ${patient.name} ${patient.lastname}`, 10, yPosition);

        // Visit Date
        yPosition += 10;
        doc.setFontSize(12);
        doc.setFont('Helvetica', 'normal');
        doc.text(`Visit Date: ${formatVisitDate(visit.visit_date)}`, 10, yPosition);

        // Add divider
        yPosition += 5;
        doc.setLineWidth(0.5);
        doc.line(10, yPosition, 200, yPosition);

        // Diagnose
        yPosition += 10;
        doc.text(`Diagnose:`, 10, yPosition);
        yPosition += 5;
        doc.setFont('Helvetica', 'normal');
        doc.text((visit.diagnose || '').split('\n'), 10, yPosition);

        // Add divider
        yPosition += 10 + ((visit.diagnose || '').split('\n').length * 5); // Adjust for text length
        doc.line(10, yPosition, 200, yPosition);

        // ECHO Report
        yPosition += 10;
        doc.text(`ECHO Report:`, 10, yPosition);
        yPosition += 5;
        doc.text((visit.echo_report || '').split('\n'), 10, yPosition);

        // Add divider
        yPosition += 10 + ((visit.echo_report || '').split('\n').length * 5);
        doc.line(10, yPosition, 200, yPosition);

        // Treatment
        yPosition += 10;
        doc.text(`Treatment:`, 10, yPosition);
        yPosition += 5;
        doc.text((visit.treatment || '').split('\n'), 10, yPosition);

        // Footer text at the bottom-right of the page
        doc.setFont('Helvetica', 'bold');
        doc.text('Dr. Spec. Endocrinolog: Beqir Mahmudi', 180, 280, { align: 'right' }); // Adjust position to bottom-right

        // Save the PDF
        doc.save(`${patient.name}_${patient.lastname}_Visit_${formatVisitDate(visit.visit_date)}.pdf`);
    };


    return <StaffLayout>
        <Link className="staff-back-link" to="/patients"><ArrowBack />Back to patients</Link>
        <PageHeading eyebrow="PATIENT CARE / RECORDS" title="Patient records" description="Their story of care, one visit at a time." action={<Button variant="contained" startIcon={<FaPlus />} onClick={() => setShowVisitModal(true)}>Add Visit</Button>} />
        <section className="staff-patient-summary" aria-label="Patient details">
            <PersonName name={`${patient.name || ''} ${patient.lastname || ''}`} subtitle={`Patient #${id}`} />
            <div className="staff-patient-details"><div><small>ID NUMBER</small>{patient.id_number || '\u2014'}</div><div><small>PHONE</small>{patient.phone || '\u2014'}</div><div><small>LAST VISIT</small>{formatClinicDate(visits[0]?.visit_date || patient.last_visit)}</div></div>
        </section>
        <div className="staff-section-heading"><h2>Visit history<span className="staff-count">{visits.length}</span></h2><span>Most recent first</span></div>
        {visitsError && <Alert severity="error" sx={{ mb: 3 }} action={<Button onClick={() => setRetry(n => n + 1)}>Retry</Button>}>{visitsError}</Alert>}
        {loading ? <div className="staff-loading" role="status"><CircularProgress size={24} />Loading visit history...</div> : !visitsError && <div className="staff-visit-list">{visits.length ? visits.map((visit, index) => <article className="staff-visit-card" key={visit.id}>
            <div className="staff-visit-header"><EventNoteOutlined /><h3>{formatClinicDate(visit.visit_date)}</h3><span className="staff-pill">{index === 0 ? 'Latest visit' : `Visit #${visit.id}`}</span></div>
            <div className="staff-visit-body">{[['Diagnosis', 'diagnose'], ['Anamnesis', 'anamnesa'], ['ECHO report', 'echo_report'], ['Treatment', 'treatment']].map(([label, field]) => <div className="staff-visit-field" key={field}><h4>{label}</h4><p>{visit[field] || 'No information recorded.'}</p></div>)}</div>
            <div className="staff-visit-actions"><Button variant="outlined" startIcon={<FaPrint />} onClick={() => handlePrint(visit)}>Print</Button><Button variant="outlined" startIcon={<FaFilePdf />} onClick={() => handlePDF(visit)}>Download PDF</Button><Button startIcon={<FaEdit />} onClick={() => { setSelectedVisit(visit); setShowEditVisitModal(true); }}>Edit visit</Button><Button className="staff-delete" color="secondary" startIcon={<FaTrash />} onClick={() => { setSelectedVisit(visit); setShowDeleteModal(true); }}>Delete</Button></div>
        </article>) : <div className="staff-panel"><EmptyState title="The first chapter starts here" description="Add a visit to begin this patient's care history." /></div>}</div>}
        <AddVisitModal patientId={id} open={showVisitModal} onClose={() => setShowVisitModal(false)} onVisitAdded={handleVisitAdded} />
        <EditVisitModal open={showEditVisitModal} onClose={() => setShowEditVisitModal(false)} visit={selectedVisit} onVisitEdited={handleVisitEdited} />
        <DeleteConfirmationModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDeleteVisit} visit={selectedVisit} />
    </StaffLayout>;
};

export default PatientRecords;
